import { ref, reactive, computed, getCurrentInstance, onMounted } from "vue";
import { defineStore } from "pinia";
import { useSettings } from "@store/settings.js";
import Cookies from "@utils/Cookies.js";
import moment from "moment";
import { getHashQueryParam, removeHashQueryParam } from "../../utilities";
import Ajax from "@utils/ajax";

const isLocked = prebook.is_pro != 1;

Ajax.setPrefix("prebook_");

export const useAppointments = defineStore("appointments", () => {
  const $settings = useSettings();
  const $prebook = computed(() => window.prebook || {});

  const activeStep = ref("service");

  const isServiceBeforeStaff = computed(() => {
    return (
      sortedRoutes.value.map((route) => route.route_name).indexOf("staff") >
      sortedRoutes.value.map((route) => route.route_name).indexOf("service")
    );
  });

  const isDateBeforeService = computed(() => {
    return (
      sortedRoutes.value.map((route) => route.route_name).indexOf("date") >
      sortedRoutes.value.map((route) => route.route_name).indexOf("service")
    );
  });

  const instance = getCurrentInstance();

  // Service list.
  const services = ref([]);

  const getServices = computed(() => {
    const config = instance.appContext.config.globalProperties.config || {};

    var _services = services.value || [];
    // console.log(config)
    // console.log(_services)
    // Step 1: Filter by Categories
    if (config.categories && config.categories !== "") {
      //   console.log("1");
      const categoryIds = config.categories
        .toString()
        .split(",")
        .map((id) => parseInt(id)); // Convert to numbers

      // Filter services by category first
      _services = _services.filter((service) =>
        categoryIds.includes(service.category_id)
      );
    }

    // Step 2: Filter by Services
    if (config.services && config.services !== "") {
      //   console.log("2");
      const serviceIds = config.services
        .toString()
        .split(",")
        .map((id) => parseInt(id)); // Convert to numbers
      // If services are selected, filter again using service_id on the previously category-filtered services
      _services = _services.filter((service) =>
        serviceIds.includes(service.service_id)
      );
    }

    // If only a staff is configured, filter services by staffs
    if (config.staffs && config.staffs !== "" && !isServiceBeforeStaff.value) {
      //   console.log("3");
      const staffIds = config.staffs
        .toString()
        .split(",")
        .map((id) => parseInt(id)); // Convert to numbers
      // If services are selected, filter again using service_id on the previously category-filtered services
      _services = _services.filter((service) =>
        service.staffs.some((staff_id) => staffIds.includes(staff_id))
      );
    }
    // console.log(isServiceBeforeStaff.value, selectedStaff.value.staff_id);
    if (!isServiceBeforeStaff.value && Boolean(selectedStaff.value.staff_id)) {
      //   console.log("4", {
      //     isServiceBeforeStaff: !isServiceBeforeStaff.value,
      //     selectedStaff: selectedStaff.value.staff_id,
      //     status:
      //       !isServiceBeforeStaff.value && Boolean(selectedStaff.value.staff_id),
      //   });
      // Step 3: Filter by SelectedStaff
      _services = _services.filter((service) => {
        let s = services.value.find(
          (ser) => ser.service_id == service.service_id
        );
        // console.log(s);
        return s.staffs.includes(selectedStaff.value.staff_id);
      });
    }

    if (_services.length == 1) {
      //   console.log("5");
      setAppointment("service_id", _services[0].service_id);
      // nextStep();
    }

    return _services;
  });

  // Staffs list.
  const staffs = ref([]);
  const getStaffs = computed(() => {
    const config = instance.appContext.config.globalProperties.config || {};

    var _staffs = staffs.value || [];
    // console.log(_staffs);
    // filter by config
    if (config.staffs && config.staffs !== undefined) {
      //   console.log("staff 1");
      _staffs =
        _staffs.filter((staff) =>
          config?.staffs
            .toString()
            .split(",")
            .map((id) => parseInt(id))
            .includes(staff.staff_id)
        ) || [];
    }
    // filter by selected service
    if (selectedService.value.service_id && isServiceBeforeStaff.value) {
      //   console.log("staff 2", {
      //     selectedService: selectedService.value.service_id,
      //     isServiceBeforeStaff: isServiceBeforeStaff.value,
      //   });
      _staffs =
        _staffs?.filter((s) =>
          selectedService.value?.staffs?.includes(s.staff_id)
        ) || [];
    }

    // if only one then preselect
    if (_staffs.length == 1) {
      //   console.log("staff 3");
      setAppointment("staff_id", _staffs[0].staff_id);
      // nextStep();
    }
    // console.log(_staffs);
    return _staffs;
  });

  // additional information.
  const customFields = ref([
    {
      id: 0,
      label: "Have you taken Covid test?",
      required: true,
      type: "select",
      options: ["Yes", "No"],
      value: "",
    },
    {
      id: 1,
      label: "Have you taken vaccine?",
      required: true,
      type: "select",
      options: ["Yes", "No"],
      value: "",
    },
    {
      id: 2,
      label: "Any other information",
      required: false,
      type: "input",
      value: "",
    },
  ]);

  // appointment types
  const appointmentTypes = ref([
    {
      id: 1,
      name: "Onsite",
      description: "Address of service provider",
      icon: "building02",
      checkbox: false,
    },
    {
      id: 2,
      name: "Virtual",
      description: "Google Meet",
      icon: "videoRecorder",
      checkbox: false,
    },
    {
      id: 3,
      name: "Home Service",
      description: "Add home address",
      icon: "home01",
      checkbox: false,
      address: "",
    },
  ]);

  // appointment.
  const appointment = ref({
    customer_id: $prebook.value.customer.customer_id,
    service_id: null,
    staff_id: null,
    booking_date: null,
    booking_time_start: null,
    booking_time_end: null,
    appointment_type: null,
    payment_method:
      $prebook.value.settings.payment.default_payment_method || "cash",
    // custom_fields: {}
  });

  onMounted(() => {
    appointment.value.customer_id =
      $prebook.value.customer?.customer_id || null;
  });

  const confirmation = ref({});

  // set & get appointment in local storage
  const cookiesAppointment = async (type) => {
    // set
    if (type == "set") {
      await Cookies.set(
        "prebook_appointment",
        JSON.stringify(appointment.value),
        0.5
      );
    }

    // get
    else if (type === "get") {
      if (!Cookies.get("prebook_appointment")) {
        await cookiesAppointment("set");
      } else {
        return (appointment.value = await JSON.parse(
          Cookies.get("prebook_appointment")
        ));
      }
    }
  };

  const setAppointment = (key, value, next = true) => {
    // console.log("setAppointment", key, value, next);
    let sorted = {};
    sortedRoutes.value.forEach((route) => {
      sorted[route.route_name] = route.priority;
    });
    // console.log("sorted", sorted, sortedRoutes.value);

    const resetAppointmentValues = () => {
      appointment.value.booking_time_start = null;
      appointment.value.booking_time_end = null;
      appointment.value.appointment_type = null;
      // appointment.value.custom_fields = {}
    };

    const resetConditions = {
      service_id: () => {
        if (sorted["date"] > sorted["service"]) {
          appointment.value.booking_date = null;
        }
        if (sorted["staff"] > sorted["service"]) {
          appointment.value.staff_id = null;
        }
      },
      staff_id: () => {
        // console.log(1, sorted["date"], sorted["staff"]);
        if (sorted["date"] > sorted["staff"]) {
          appointment.value.booking_date = null;
        }
        // console.log(2, sorted["service"], sorted["staff"]);
        if (sorted["service"] > sorted["staff"]) {
          appointment.value.service_id = null;
        }
      },
      booking_date: () => {
        // console.log(sorted['date'], sorted['staff'], sorted['service'])
        if (sorted["date"] < sorted["staff"]) {
          appointment.value.staff_id = null;
        }
        if (sorted["date"] < sorted["service"]) {
          appointment.value.service_id = null;
        }
        // if (next) nextStep();
      },
    };

    if (resetConditions[key]) {
      resetConditions[key]();
      resetAppointmentValues();
    }

    appointment.value[key] = value;
    cookiesAppointment("set");
    // matchAppointmentPreRequirement();
    // if(next && !['booking_time_start','booking_time_end'].includes(key)) nextStep();
    // if(key === 'booking_date') nextStep();
    if (next) nextStep();
  };

  // Business hours
  const businessHours = computed(() => {});

  // Selected service
  const selectedService = computed(() => {
    if (services.value.length > 0) {
      return (
        services.value.find(
          (service) => service.service_id == appointment.value.service_id
        ) || { service_id: "" }
      );
    }

    return { service_id: "" };
  });

  // Selected staff
  const selectedStaff = computed(() => {
    if (staffs.value.length > 0) {
      return (
        staffs.value.find(
          (staff) => staff.staff_id == appointment.value.staff_id
        ) || { staff_id: "" }
      );
    }

    return { staff_id: "" };
  });

  // Available days
  const availableDays = computed(() => {
    let working_days = staffs.value.find(
      (staff) => staff.staff_id === appointment.value.staff_id
    ).working_days;

    return Object.entries(working_days)
      .filter(([day, info]) => info.enabled)
      .map(([day]) => day);
  });

  // Selected date
  const selectedDate = computed(() => {
    return appointment.value?.booking_date;
  });

  // Available times
  const availableTimes = computed(() => {
    return (
      staffs.value.find(
        (staff) => staff.staff_id === appointment.value.staff_id
      )?.working_days[selectedDate.value.day]?.slots || []
    );
  });

  // Selected time
  const selectedTime = computed(() => {
    return {
      booking_time_start: appointment.value.booking_time_start,
      booking_time_end: appointment.value.booking_time_end,
    };
  });

  // Selected appointment type
  const selectedAppointmentType = computed(() => {
    return appointment.value.appointment_type || { id: null };
  });

  const sortedRoutes = computed(() => {
    let sorted = [];

    let routeMap = {
      service: "service",
      staff: "staff",
      date: "date",
      time: "time",
      account: "customer_information",
      overview: "booking_overview",
      confirmation: "confirmation",
    };

    for (let key in routeMap) {
      sorted.push({
        route_name: key,
        priority:
          $settings.customizer[routeMap[key]]?.priority ||
          (sorted.length
            ? Math.max(...sorted.map((item) => item.priority)) + 1
            : 0),
      });
    }

    if (!$settings.addons?.staffs) {
      sorted = sorted.filter((route) => route.route_name !== "staff");
    }

    return sorted.sort((a, b) => a.priority - b.priority) || [];
  });

  // available routes
  const availableRoutes = computed(() => {
    let _routes = sortedRoutes.value.map((route) => route.route_name) || [
      "service",
      "staff",
      "date",
      "time",
      "account",
      "overview",
      "confirmation",
    ];

    if (!$settings.addons.staffs) {
      _routes = _routes.filter((route) => route !== "staff");
    }

    if (window.prebook.customer.customer_id) {
      _routes = _routes.filter((route) => route !== "account");
    }

    return _routes;
  });

  // Go to next step.
  const nextStep = () => {
    // console.log(activeStep.value)
    const currentIndex = sortedRoutes.value
      .map((r) => r.route_name)
      .indexOf(activeStep.value);
    // console.log(sortedRoutes.value.map(r => r.route_name));
    // if(!$settings.addons?.staffs) {
    //     sortedRoutes.value = sortedRoutes.value.filter(route => route.route_name !== 'staff');
    // }
    const nextIndex = currentIndex + 1;
    // console.log(nextIndex);
    // console.log(nextIndex < sortedRoutes.value.length)
    // console.log({
    //     currentIndex,
    //     nextIndex,
    //     length: sortedRoutes.value.length,
    //     next:sortedRoutes.value[nextIndex].route_name
    // })

    if (activeStep.value == "staff-profile") {
      let staffIndex = sortedRoutes.value
        .map((r) => r.route_name)
        .indexOf("staff");

      activeStep.value = sortedRoutes.value[staffIndex + 1].route_name;
      return;
    }

    if (nextIndex < sortedRoutes.value.length) {
      activeStep.value = sortedRoutes.value[nextIndex].route_name;
    }
  };

  // Go to previous step.
  const prevStep = () => {
    const currentIndex = sortedRoutes.value
      .map((r) => r.route_name)
      .indexOf(activeStep.value);
    const prevIndex = currentIndex - 1;
    if (prevIndex >= 0) {
      activeStep.value = sortedRoutes.value[prevIndex].route_name;
    }
  };

  // Is first step.
  const isFirstStep = computed(() => {
    return activeStep.value === "service";
  });

  // Is last step.
  const isLastStep = computed(() => {
    return activeStep.value === "confirmation";
  });

  // Service loader.
  const isServiceLoaded = ref(false);

  const categories = ref([]);

  // Fetch Services
  const fetchServices = async () => {
    if (services.value.length > 0) {
      return;
    }

    const response = await Ajax.get("get_services");

    services.value = Object.values(response.data.services) || [];

    isServiceLoaded.value = true;
    return true;
  };

  // Staff loader.
  const isStaffLoaded = ref(false);

  // Fetch Staffs
  const fetchStaffs = async () => {
    if (staffs.value.length > 0) {
      return;
    }

    const response = await Ajax.get("get_staffs");

    staffs.value = Object.values(response.data) || [];
    isStaffLoaded.value = true;
    return true;
  };

  // Overview Loader
  const isOverviewLoaded = computed(() => {
    return isServiceLoaded.value && isStaffLoaded.value;
  });

  const availableDates = ref([]);
  const isAvailableDatesLoaded = ref(false);

  // Fetch available dates
  const fetchAvailableDates = async (
    service_id = null,
    staff_id = null,
    max_days = null
  ) => {
    service_id = service_id || appointment.value.service_id;
    staff_id = staff_id || appointment.value.staff_id;

    isAvailableDatesLoaded.value = false;

    const response = await Ajax.get("get_available_dates", {
      service_id,
      staff_id,
      max_days,
    });

    isAvailableDatesLoaded.value = true;
    if (!response.success) return (availableDates.value = []);

    return (availableDates.value = response.data.filter((date) =>
      moment(date.date, "YYYY-MM-DD").isSameOrAfter(moment(), "day")
    ));
  };

  const availableSlots = ref([]);
  const isAvailableSlotsLoaded = ref(false);

  // Fetch available slots
  const fetchAvailableSlots = async (
    date = null,
    service_id = null,
    staff_id = null,
    customer_id = null
  ) => {
    date = date || appointment.value.booking_date?.fullDate;
    service_id = service_id || appointment.value.service_id;
    staff_id = staff_id || appointment.value.staff_id;
    customer_id = customer_id || appointment.value.customer_id;

    isAvailableSlotsLoaded.value = false;

    const response = await Ajax.get("get_available_slots", {
      date,
      service_id,
      staff_id,
      customer_id,
    });

    isAvailableSlotsLoaded.value = true;
    if (!response.success) return (availableSlots.value = []);

    let times = [];

    response.data.forEach((slot) => {
      let currentTime = moment();
      let startTime = moment(slot.start, "HH:mm");
      let endTime = moment(slot.end, "HH:mm");
      let date = moment(appointment.value.booking_date?.fullDate, "YYYY-MM-DD");
      let isToday = date.isSame(moment(), "day");

      if (!slot.available || (isToday && startTime.isBefore(currentTime)))
        return;
      times.push(slot);
    });

    return (availableSlots.value = times);
  };

  const loadSelectedAppointment = async () => {
    await cookiesAppointment("get");

    appointment.value.customer_id = $prebook.value.customer.customer_id;

    fetchServices();
    fetchStaffs();

    if (appointment.value.booking_date) {
      fetchAvailableDates();
    }

    if (appointment.value.time) {
      fetchAvailableSlots();
    }
  };

  // Booking Loaded
  const isConfirmationLoaded = ref(false);
  const state = reactive({
    loading: false,
    error: "",
  });
  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };
  // Submit Booking Confirmation
  const submitBookingConfirmation = async () => {
    // bail, it user is Administrator
    if ($prebook.value.customer.role !== "customer") {
      state.error = `${capitalizeFirstLetter(
        $prebook.value.customer.role
      )} cannot book ${String(
        $settings.labels.appointment?.plural
      ).toLowerCase()} as ${String(
        $settings.labels.customer?.plural
      ).toLowerCase()}. For testing purposes, please use a different user account.`;
      return;
    }

    // Page URL without hash or query
    let pageUrl = window.location.href.split("#")[0];
    pageUrl = pageUrl.split("?")[0];

    state.error = "";
    state.loading = true;
    isConfirmationLoaded.value = false;
    const response = await Ajax.post("book_appointment", {
      customer_id: $prebook.value.customer.customer_id,
      service_id: appointment.value.service_id,
      staff_id: appointment.value.staff_id,
      booking_date: appointment.value.booking_date?.fullDate,
      booking_time_start: appointment.value.booking_time_start,
      booking_time_end: appointment.value.booking_time_end,
      appointment_type: "Onsite",
      payment_method: $settings.addons.payment
        ? appointment.value.payment_method
        : "cash",
      meta: {
        customer_first_name: $prebook.value.customer.first_name,
        customer_last_name: $prebook.value.customer.last_name,
        customer_phone: $prebook.value.customer.customer_id,
        customer_email: $prebook.value.customer.email,
      },
      stripe_success_url: pageUrl + "#confirmation?prebook_payment=success",
      stripe_cancel_url: pageUrl + "#confirmation?prebook_payment=cancel",
    });
    // Log('Confirmation', response.data)

    if (!response.success) {
      state.error = response.message || "An error occurred";
      state.loading = false;
      return (confirmation.value = {});
    } else {
      // check if payment url exists
      if (response.data.payment_url) {
        // before redirecting to payment page, store response data in local storage
        localStorage.setItem(
          "prebook_stripe_confirmation_data",
          JSON.stringify(response.data)
        );

        // redirect to payment page
        window.location.href = response.data.payment_url;
        return;
      }

      // set response to confirmation store because confirmation page will show response data
      confirmation.value = response.data;

      // reset appointment from cookies
      appointment.value = {
        service_id: null,
        staff_id: null,
        booking_date: null,
        booking_time_start: null,
        payment_method:
          $prebook.value.settings.payment.default_payment_method || "cash",
        booking_time_end: null,
      };

      cookiesAppointment("set");
      nextStep();
    }
    isConfirmationLoaded.value = true;
    state.loading = false;
  };

  // for stripe payment success load data from localstorage
  const isStripePaymentSuccess = () => {
    const paymentStatus = getHashQueryParam("prebook_payment");
    const isSuccess = paymentStatus === "success";

    if (
      !localStorage.getItem("prebook_stripe_confirmation_data") ||
      !isSuccess
    ) {
      removeHashQueryParam("prebook_payment");
      return false;
    }

    if (activeStep.value !== "confirmation" || isSuccess) {
      activeStep.value = "confirmation";
      confirmation.value = JSON.parse(
        localStorage.getItem("prebook_stripe_confirmation_data")
      );
      isConfirmationLoaded.value = true;
      return true;
    }

    return false;
  };

  const matchAppointmentPreRequirement = async () => {
    // console.log('matchAppointmentPreRequirement');
    // bail, if payment is success
    if (isStripePaymentSuccess()) return;

    await cookiesAppointment("get");

    appointment.value.customer_id =
      $prebook.value.customer?.customer_id || null;

    let mapping = {
      service: Boolean(appointment.value.service_id),
      staff:
        Boolean(appointment.value.staff_id) && Boolean($settings.addons.staffs),
      date: Boolean(appointment.value.booking_date),
      time: Boolean(appointment.value.booking_time_start),
      account: Boolean(appointment.value.customer_id),
      overview:
        Boolean(appointment.value.service_id) &&
        Boolean(appointment.value.staff_id) &&
        Boolean(appointment.value.booking_date) &&
        Boolean(appointment.value.booking_time_start),
      // 'confirmation': !appointment.value.service_id || !appointment.value.staff_id || !appointment.value.booking_date || !appointment.value.booking_time_start
    };

    // console.log('availableRoutes',availableRoutes.value)
    // map customizer routing
    const steps = availableRoutes.value
      .filter((r) => r !== "confirmation")
      .map((r) => {
        return {
          priority: sortedRoutes.value.find((s) => s.route_name === r)
            ?.priority,
          route_name: r,
          is_completed: mapping[r],
        };
      });

    // console.log('steps',steps)

    const nextStep = steps.find((step) => !step.is_completed);
    // console.log('nextStep',nextStep)
    if (nextStep) {
      return (activeStep.value = nextStep.route_name);
    } else {
      return (activeStep.value = "overview");
    }
  };

  // Export.
  return {
    state,
    services,
    categories,
    staffs,
    customFields,
    appointment,
    setAppointment,
    businessHours,
    selectedService,
    selectedStaff,
    availableDays,
    selectedDate,
    availableTimes,
    selectedTime,
    selectedAppointmentType,
    appointmentTypes,
    isServiceLoaded,
    isStaffLoaded,
    isOverviewLoaded,
    availableSlots,
    isAvailableSlotsLoaded,
    availableDates,
    confirmation,
    isAvailableDatesLoaded,
    isConfirmationLoaded,
    matchAppointmentPreRequirement,
    isStripePaymentSuccess,

    nextStep,
    prevStep,
    isFirstStep,
    isLastStep,
    cookiesAppointment,
    fetchServices,
    fetchStaffs,
    submitBookingConfirmation,
    fetchAvailableSlots,
    fetchAvailableDates,
    loadSelectedAppointment,
    getServices,
    getStaffs,
    sortedRoutes,
    activeStep,
  };
});
